import * as d3 from 'd3';
import _ from 'lodash';
import { Granularity } from 'models';
import React from 'react';
import { NodeGroup } from 'react-move';
import * as S from './chart-line-group.styles';
import { IChartPoint, IChartTimeline } from './models';
import { ChartConstants, createScales } from './util';

export interface INode {
    key: string;
    state: INodeConfig;
}

export interface INodeConfig {
    opacity: number;
    x: number[] | number[][];
    y: number[] | number[][];
    timing?: object;
}

export interface IChartLineGroupProps {
    chartRect: ClientRect;
    granularity: Granularity;
    timeline: IChartTimeline;
}

export class ChartLineGroup extends React.Component<IChartLineGroupProps> {
    public render(): React.ReactElement {
        return (
            <S.LineGroup>
                <S.GradientDefs>
                    <S.LinearGradient id="lineGroupGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <S.GradientTop offset="0%" />
                        <S.GradientMiddle offset="40%" />
                        <S.GradientBottom offset="100%" />
                    </S.LinearGradient>
                </S.GradientDefs>
                {this.renderNodeGroup()}
            </S.LineGroup>
        );
    }
    private readonly renderNodeGroup = (): React.ReactElement => {
        const { timeline } = this.props;
        return (
            <NodeGroup
                data={timeline.data}
                keyAccessor={this.keyAccessor}
                start={this.startTransition}
                enter={this.enterTransition}
                update={this.updateTransition}
                leave={this.leaveTransition}
                interpolation={this.interpolation}
                children={this.renderNodes}
            />
        );
    };

    private readonly keyAccessor = (points: IChartPoint[], i: number): string => {
        const { granularity } = this.props;
        return `${granularity}-${i}`;
    };
    private readonly startTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 1e-6,
            x: points.map((p: IChartPoint): number => xScale(p.date)),
            y: points.map((p: IChartPoint): number => chartRect.bottom),
        };
    };
    private readonly enterTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 1,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => yScale(p.amount))],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly updateTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 0.8,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => yScale(p.amount))],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly leaveTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 0.3,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => chartRect.bottom)],
            timing: {
                duration: 400,
                ease: d3.easeExpInOut,
            },
        };
    };
    private interpolation(
        start: number | number[],
        end: number | number[],
        attr: string | undefined,
    ): ((t: number) => number) | ((t: number) => number[]) {
        if (attr === 'x' || attr === 'y') {
            return d3.interpolate<number[]>(start as number[], end as number[]);
        }
        return d3.interpolate(start as number, end as number);
    }
    private readonly renderNodes = (nodes: INode[]): React.ReactElement => {
        const { chartRect } = this.props;
        const lineGroups = nodes.map(({ key, state }, i) => {
            const path = d3
                .line<number[]>()
                .x((arr: number[]): number => arr[0])
                .y((arr: number[]): number => arr[1]);
            const line: number[][] = _.zip(state.x as number[], state.y as number[]) as number[][];
            const shadowBoundary = ([] as number[][]).concat(
                [[(_.first(line) as number[])[0], chartRect.bottom]],
                line,
                [[(_.last(line) as number[])[0], chartRect.bottom]],
            );
            const shadowPoints =
                i === 0 ? shadowBoundary.map((arr: number[]) => `${arr[0]},${arr[1]}`).join(' ') : undefined;
            const dots: React.ReactElement[] = line.map(
                (p: number[], j: number): React.ReactElement => {
                    return (
                        <S.LineDot key={`${key}-${i}-${j}`} ord={i} r={ChartConstants.dotRadius} cx={p[0]} cy={p[1]} />
                    );
                },
            );
            return (
                <S.Line key={`${key}-${i}`} opacity={state.opacity}>
                    {shadowPoints && <S.LineShadow points={shadowPoints} />}
                    <S.LinePath d={path(line) as string} ord={i} />
                    {dots}
                </S.Line>
            );
        });
        return <>{lineGroups}</>;
    };
}
